'use client';

import { useState, useEffect, useCallback } from 'react';
import * as Sentry from '@sentry/react';
import { supabase } from '@/lib/supabaseClient';
import { toast } from 'sonner';

// ============================================================
// TIPOS
// ============================================================

export interface RegistroListaNegra {
  id_lista_negra: string;
  id_empresa: string;
  telefono: string | null;
  email: string | null;
  motivo: string | null;
  activo: boolean;
  fecha_creacion: string;
  usuario_creacion?: string | null;
}

// ============================================================
// HELPERS
// ============================================================

const isValidPhone = (phone: string) => {
  const cleaned = phone.replace(/\s+/g, '');
  return /^\+?[1-9]\d{6,14}$/.test(cleaned) || /^[679]\d{8}$/.test(cleaned);
};

const isValidEmail = (email: string) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

const normalizePhone = (value: string): string => {
  let cleaned = value.trim();
  if (cleaned.startsWith('00')) cleaned = '+' + cleaned.slice(2);
  cleaned = cleaned.replace(/[^\d+]/g, '');
  if (cleaned.startsWith('+'))
    cleaned = '+' + cleaned.slice(1).replace(/\+/g, '');
  return cleaned;
};

// ============================================================
// HOOK PRINCIPAL
// ============================================================

export function useListaNegra(empresaId: string) {
  const [registros, setRegistros] = useState<RegistroListaNegra[]>([]);
  const [loading, setLoading] = useState(false);

  // ============================================================
  // FETCH
  // ============================================================
  const fetchRegistros = useCallback(async () => {
    if (!empresaId) return;

    console.trace();

    setLoading(true);

    const { data, error } = await supabase
      .from('vista_lista_negra')
      .select('*')
      .eq('id_empresa', empresaId)
      .order('fecha_creacion', { ascending: false });

    if (error) {
      Sentry.captureException(error, { extra: { empresaId } });
      toast.error('No se pudo cargar la lista negra');
      setRegistros([]);
    } else {
      setRegistros((data as RegistroListaNegra[]) ?? []);
    }

    setLoading(false);
  }, [empresaId]);

  useEffect(() => {
    fetchRegistros();
  }, [fetchRegistros]);

  // ============================================================
  // INSERTAR
  // ============================================================
  const insertar = useCallback(
    async (
      idEmpresa: string,
      userId: string,
      data: {
        telefono?: string;
        email?: string;
        motivo?: string;
        activo: boolean;
      }
    ) => {
      const toastId = toast.loading('Añadiendo número…');

      const telefono = data.telefono ? normalizePhone(data.telefono) : null;
      const email = data.email ? data.email.trim() : null;

      if (!telefono && !email) {
        toast.dismiss(toastId);
        toast.error('Indica al menos un teléfono o un email');
        return { error: true };
      }

      if (telefono && !isValidPhone(telefono)) {
        toast.dismiss(toastId);
        toast.error('Ese teléfono no parece válido');
        return { error: true };
      }

      if (email && !isValidEmail(email)) {
        toast.dismiss(toastId);
        toast.error('Ese email no parece válido');
        return { error: true };
      }

      const query = supabase
        .from('lista_negra')
        .select('id_lista_negra')
        .eq('id_empresa', idEmpresa);

      if (telefono) query.or(`telefono.eq.${telefono}`);
      if (email) query.or(`email.eq.${email}`);

      const { data: exists, error: dupError } = await query;

      if (dupError) {
        Sentry.captureException(dupError, { extra: { idEmpresa } });
        toast.dismiss(toastId);
        toast.error('No se pudo comprobar si ya existe');
        return { error: true };
      }

      if (exists && exists.length > 0) {
        toast.dismiss(toastId);
        toast.error('Ese teléfono o email ya está en la lista negra');
        return { error: true };
      }

      const { error } = await supabase.from('lista_negra').insert([
        {
          id_empresa: idEmpresa,
          telefono,
          email,
          motivo: data.motivo || null,
          activo: data.activo,
          usuario_creacion: userId,
        },
      ]);

      toast.dismiss(toastId);

      if (error) {
        Sentry.captureException(error, { extra: { idEmpresa, userId } });
        toast.error('No se pudo añadir el número');
        return { error: true };
      }

      toast.success('Número añadido a la lista negra');
      fetchRegistros();
      return { error: false };
    },
    [fetchRegistros]
  );

  // ============================================================
  // ACTUALIZAR
  // ============================================================
  const actualizar = useCallback(
    async (
      id_lista_negra: string,
      userId: string,
      data: {
        telefono?: string;
        email?: string;
        motivo?: string;
        activo: boolean;
      }
    ) => {
      const toastId = toast.loading('Guardando cambios…');

      const telefono = data.telefono ? normalizePhone(data.telefono) : null;
      const email = data.email ? data.email.trim() : null;

      if (!telefono && !email) {
        toast.dismiss(toastId);
        toast.error('Indica al menos un teléfono o un email');
        return { error: true };
      }

      if (telefono && !isValidPhone(telefono)) {
        toast.dismiss(toastId);
        toast.error('Ese teléfono no parece válido');
        return { error: true };
      }

      if (email && !isValidEmail(email)) {
        toast.dismiss(toastId);
        toast.error('Ese email no parece válido');
        return { error: true };
      }

      const { error } = await supabase
        .from('lista_negra')
        .update({
          telefono,
          email,
          motivo: data.motivo || null,
          activo: data.activo,
          usuario_edicion: userId,
        })
        .eq('id_lista_negra', id_lista_negra);

      toast.dismiss(toastId);

      if (error) {
        Sentry.captureException(error, { extra: { id_lista_negra, userId } });
        toast.error('No se pudieron guardar los cambios');
        return { error: true };
      }

      toast.success('Cambios guardados');
      fetchRegistros();
      return { error: false };
    },
    [fetchRegistros]
  );

  // ============================================================
  // ELIMINAR
  // ============================================================
  const eliminar = useCallback(
    async (id_lista_negra: string) => {
      const toastId = toast.loading('Eliminando…');

      const { error } = await supabase
        .from('lista_negra')
        .delete()
        .eq('id_lista_negra', id_lista_negra);

      toast.dismiss(toastId);

      if (error) {
        Sentry.captureException(error, {
          extra: { id_lista_negra, empresaId },
        });
        toast.error('No se pudo eliminar el número');
        return { error: true };
      }

      toast.success('Número eliminado de la lista negra');
      fetchRegistros();
      return { error: false };
    },
    [fetchRegistros, empresaId]
  );

  // ============================================================
  // RETURN
  // ============================================================
  return {
    registros,
    loading,
    refetch: fetchRegistros,
    insertar,
    actualizar,
    eliminar,
  };
}